import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TUTORIAL_STEPS } from './components/tutorial/Consts';

import { useAudio } from '../../hooks/useAudio';
import { useResize } from '../../hooks/useResize';
import { useTyping } from './hooks/useTyping';
import { TypingResult, TypingResultTutorialTypeEnum } from '../../api';
import { TypingProgress, TypingTextEngine } from '../../types/Typing';
import { Tutorial } from './components/tutorial/Tutorial';
import {
  INPUT_KEY_RESULT,
  GAME_START_KEY,
  CAPS_LOCK_KEY,
  SHIFT_KEY,
  COUNT_DOWNGAME_START,
  RESULT_TYPE,
  NON_ERROR_BONUS_SCORE,
  COMPLETE_TYPE_SCORE,
  UNMATCH_TYPE_SCORE,
  SE_LIST,
  THEMES,
} from '../../constants/Consts';
import { ChangeImageWhenHover } from '../../components/ChangeImageWhenHover';
import { DisplayWord } from './components/DisplayWord';
import { StartModal } from './components/StartModal';
import { ResultModal } from './components/ResultModal';
import { Keyboard } from './components/Keyboard';
import { SoundControlButton } from '../../components/SoundControlButton';
import { UnloadBlocker } from './components/UnloadBlocker';
import { Event } from '../events';

interface IndexProps {
  themeType: THEMES;
  character: string;
  typingResult: TypingResult;
  showFlash: boolean;
  handleShowFlash: () => void;
}

const COUNT_DOWN_TIME = 10;

const TYPING_PROGRESS_INITIAL_STATE: TypingProgress = {
  wordText: '',
  completedText: '',
  remainingText: '',
  completedRomaji: '',
  remainingRomaji: '',
};

const ERROR_DISPLAY_DURATION: number = 500;
const FINISH_EFFECT_DELAY: number = 1500;
const FINISH_COMPLETE_DELAY: number = 2000;


export const Index = ({ themeType, character, typingResult, showFlash, handleShowFlash }: IndexProps) => {
  const typingEngineRef = useRef<TypingTextEngine | null>(null);
  const mountedRef = useRef(true);
  const finishTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const { playBgmAudio, stopBgmAudio, playSeAudio, stopAllAudio } = useAudio();
  const { UNMATCH_TYPEKEY, MATCH_TYPEKEY, FINISH_TYPING, CLEAR, COUNT_DOWN, CLICK_BUTTON } = SE_LIST;

  const { resizeHandler, size, getCurrentSizeStyle } = useResize(16 / 10, 1200);
  const { typingGradeId, gradeNumber, limitSeconds, clearScore } = typingResult;
  const navigate = useNavigate();
  const location = useLocation();
  const { createTypingText, resetTypingList, isValidInputKey } = useTyping(typingGradeId);
  const [startTypingCount, setStartTypingCount] = useState<number | undefined>(undefined);
  const [isStartTyping, setIsStartTyping] = useState<boolean>(false);
  const [isFinishTyping, setIsFinishTyping] = useState<boolean>(false);
  const [typingProgress, setTypingProgress] = useState<TypingProgress | undefined>(undefined);
  const [correctTypeCount, setCorrectTypeCount] = useState<number>(0);
  const [unmatchTypeCount, setUnmatchTypeCount] = useState<number>(0);
  const [limitTime, setLimitTime] = useState<number | undefined>(undefined);
  const [inputErrorKey, setInputErrorKey] = useState<string | undefined>(undefined);
  const [shiftKeyDown, setShiftKeyDown] = useState<string>('');
  const [resultType, setResultType] = useState<RESULT_TYPE | undefined>(undefined);
  const [currentDisplayWord, setCurrentDisplayWord] = useState<string>('');
  const [currentInputWord, setCurrentInputWord] = useState<string>('');
  const [showTutorial, setShowTutorial] = useState<boolean>(false);

  const isCountdownOrStartTyping = useMemo(() => {
    return (startTypingCount !== undefined || isStartTyping) && !isFinishTyping;
  }, [startTypingCount, isStartTyping, isFinishTyping]);

  const handleStartTyping = useCallback(
    (e: KeyboardEvent) => {
      if (e.code !== GAME_START_KEY) {
        return;
      }
      if (typingResult.tutorialType !== TypingResultTutorialTypeEnum.None) {
        setShowTutorial(true);
        document.removeEventListener('keydown', handleStartTyping);
        return;
      }
      setStartTypingCount(COUNT_DOWNGAME_START);
      document.removeEventListener('keydown', handleStartTyping);
  }, [typingResult.tutorialType]);

  const handleWatchShiftMode = useCallback((e: KeyboardEvent) => {
    if (e.key === SHIFT_KEY) {
      setShiftKeyDown('');
    }
  }, []);

  const resetAllStates = useCallback(() => {
    setStartTypingCount(undefined);
    setIsStartTyping(false);
    setIsFinishTyping(false);
    setTypingProgress(undefined);
    setCorrectTypeCount(0);
    setUnmatchTypeCount(0);
    setLimitTime(undefined);
    setInputErrorKey(undefined);
    setShiftKeyDown('');
    setResultType(undefined);
    setCurrentDisplayWord('');
    setCurrentInputWord('');
  }, []);

  const updateTypingProgress = useCallback((engine: TypingTextEngine | null) => {
    if (!engine) {
      setTypingProgress(TYPING_PROGRESS_INITIAL_STATE);
      return;
    }

    setTypingProgress({
      wordText: engine.text,
      completedText: engine.completedText,
      remainingText: engine.remainingText,
      completedRomaji: engine.completedRoman,
      remainingRomaji: engine.remainingRoman,
    });
  }, []);

  const clearWord = useCallback(() => {
    typingEngineRef.current = null;
    updateTypingProgress(null);
  }, [updateTypingProgress]);

  const fetchWord = useCallback(async () => {
    try {
      const typingText = await createTypingText();
      if (!typingText || !mountedRef.current) {
        return;
      }
      typingEngineRef.current = typingText.typingText;
      updateTypingProgress(typingText.typingText);
      setCurrentDisplayWord(typingText.displayWord);
      setCurrentInputWord(typingText.word ?? '');
      setTypingProgress((prev) =>
        prev
          ? {
              ...prev,
              completedText: '',
              completedRomaji: '',
            }
          : undefined
      );
    } catch (error) {
      clearWord();
      console.error('Error occurred while fetching word:', error);
    }
  }, [createTypingText, clearWord, updateTypingProgress]);

  const handleTyping = useCallback(
    (e: KeyboardEvent) => {
      if (e.repeat || !typingEngineRef.current) {
        return;
      }

      if (e.key === SHIFT_KEY) {
        setShiftKeyDown(e.code);
        return;
      }

      const inputKey = e.getModifierState(CAPS_LOCK_KEY) ? e.key.toLocaleLowerCase() : e.key;

      if (!isValidInputKey(inputKey)) {
        // 許容していないキーは、評価しない
        return;
      }

      const inputKeyResult: string = typingEngineRef.current.inputKey(inputKey);

      switch (inputKeyResult) {
        // 不正解のキー入力
        case INPUT_KEY_RESULT.UNMATCH:
          playSeAudio(UNMATCH_TYPEKEY);
          setInputErrorKey(inputKey);
          setUnmatchTypeCount((unmatchTypeCount) => unmatchTypeCount + 1);
          return;
        // 正解のキー入力（単語未完成）
        case INPUT_KEY_RESULT.INCOMPLETE:
          playSeAudio(MATCH_TYPEKEY);
          setInputErrorKey(undefined);
          updateTypingProgress(typingEngineRef.current);
          setCorrectTypeCount((correctTypeCount) => correctTypeCount + 1);
          return;
        // 正解のキー入力（単語完成）
        case INPUT_KEY_RESULT.COMPLETE:
          playSeAudio(MATCH_TYPEKEY);
          setInputErrorKey(undefined);
          setCorrectTypeCount((correctTypeCount) => correctTypeCount + 1);
          clearWord();
          fetchWord();
          return;
      }
    },
    [fetchWord, clearWord, playSeAudio, UNMATCH_TYPEKEY, MATCH_TYPEKEY, isValidInputKey, updateTypingProgress]
  );

  const startTyping = useCallback(async () => {
    playBgmAudio(`/sounds/${themeType}/characters/${character}.mp3`);
    await fetchWord();
    if (!mountedRef.current) return;
    setStartTypingCount(undefined);
    setIsStartTyping(true);
    setLimitTime(limitSeconds);
    document.addEventListener('keydown', handleTyping);
    document.addEventListener('keyup', handleWatchShiftMode);
  }, [limitSeconds, fetchWord, handleTyping, handleWatchShiftMode, themeType, character, playBgmAudio]);

  const calcScore = useCallback(() => {
    let tempScore = correctTypeCount * COMPLETE_TYPE_SCORE - unmatchTypeCount * UNMATCH_TYPE_SCORE;
    // NOTE: 正解タイプが0回の場合は、ボーナススコアを付与しない
    if (unmatchTypeCount === 0 && correctTypeCount !== 0 && isFinishTyping) {
      tempScore += NON_ERROR_BONUS_SCORE;
    }
    if (tempScore < 0) {
      return 0;
    }
    return tempScore;
  }, [correctTypeCount, unmatchTypeCount, isFinishTyping]);

  const judgeResultType = useCallback(
    (clearScore: number) => {
      const score = calcScore();
      if (score >= clearScore) {
        return RESULT_TYPE.CLEAR;
      } else if (score > clearScore * 0.8) {
        return RESULT_TYPE.LITTLE_MORE;
      } else {
        return RESULT_TYPE.TRY_AGAIN;
      }
    },
    [calcScore]
  );

  const finishTyping = useCallback(
    (isForce: boolean = false) => {
      document.removeEventListener('keyup', handleWatchShiftMode);
      document.removeEventListener('keydown', handleTyping);
      typingEngineRef.current = null;
      setTypingProgress(undefined);

      // 強制的な初期化または、終了エフェクト中は、終了エフェクトは発動させない
      if (isForce || isFinishTyping || showFlash) {
        return;
      }
      stopBgmAudio();
      playSeAudio(FINISH_TYPING);
      const flashTimer = setTimeout(() => {
        handleShowFlash();
      }, FINISH_EFFECT_DELAY);
      const finishTimer = setTimeout(() => {
        setIsFinishTyping(true);
      }, FINISH_COMPLETE_DELAY);
      finishTimersRef.current.push(flashTimer, finishTimer);
      return () => {
        clearTimeout(flashTimer);
        clearTimeout(finishTimer);
      };
    },
    [isFinishTyping, showFlash, handleWatchShiftMode, handleTyping, handleShowFlash, playSeAudio, stopBgmAudio, FINISH_TYPING]
  );

  const reStartTypingResult = useCallback(() => {
    if (!isStartTyping) {
      return;
    }
    playBgmAudio(`/sounds/${themeType}/practice.mp3`);
    finishTyping(true);
    resetAllStates();
    resetTypingList();
    document.addEventListener('keydown', handleStartTyping);
  }, [isStartTyping, finishTyping, resetTypingList, handleStartTyping, playBgmAudio, themeType, resetAllStates]);

  // 複雑な条件の関数化
  const shouldShowSoundControl = useCallback(() => {
    const isNotStartedOrFinished = (!isStartTyping && startTypingCount === undefined) || isFinishTyping;
    return isNotStartedOrFinished;
  }, [isStartTyping, startTypingCount, isFinishTyping]);
  
  const handleJoyrideCallback = useCallback(
    () => {
      setShowTutorial(false);
      setStartTypingCount((prev) => (prev === undefined ? COUNT_DOWNGAME_START : prev));
    },[]);

  useEffect(() => {
    mountedRef.current = true;
    const finishTimers = finishTimersRef.current;
    return () => {
      mountedRef.current = false;
      typingEngineRef.current = null;
      finishTimers.forEach(clearTimeout);
      stopAllAudio();
    };
  }, [stopAllAudio]);

  useEffect(() => () => {
    document.removeEventListener('keydown', handleTyping);
    document.removeEventListener('keyup', handleWatchShiftMode);
  }, [handleTyping, handleWatchShiftMode]);

  useEffect(() => {
    document.addEventListener('keydown', handleStartTyping);
    return () => {
      document.removeEventListener('keydown', handleStartTyping);
    };
  }, [handleStartTyping]);

  useEffect(() => {
    playBgmAudio(`/sounds/${themeType}/practice.mp3`);
  }, [playBgmAudio, themeType]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    resizeHandler();

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [resizeHandler]);

  useEffect(() => {
    if (!isFinishTyping) {
      return;
    }
    const currentResult = judgeResultType(clearScore);
    if (currentResult === RESULT_TYPE.CLEAR) {
      playSeAudio(CLEAR, `/sounds/${themeType}/practice.mp3`);
    } else {
      playBgmAudio(`/sounds/${themeType}/practice.mp3`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinishTyping]);

  useEffect(() => {
    if (startTypingCount === undefined) {
      return;
    }

    stopBgmAudio();
    playSeAudio(COUNT_DOWN);
    if (startTypingCount > 0) {
      const timer = setTimeout(() => {
        setStartTypingCount(startTypingCount - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setStartTypingCount(0);
        startTyping();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [startTyping, startTypingCount, playBgmAudio, stopBgmAudio, playSeAudio, COUNT_DOWN]);

  useEffect(() => {
    if (limitTime === undefined) {
      return;
    }
    if (limitTime > 0) {
      const timer = setTimeout(() => {
        setLimitTime(limitTime - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      finishTyping();
    }
  }, [limitTime, finishTyping]);

  useEffect(() => {
    if (!inputErrorKey) {
      return;
    }
    const timer = setTimeout(() => {
      setInputErrorKey(undefined);
    }, ERROR_DISPLAY_DURATION);
    return () => clearTimeout(timer);
  }, [inputErrorKey]);

  useEffect(() => {
    if (!location.state?.reset) {
      return;
    }
    reStartTypingResult();
    location.state.reset = false;
  }, [location.state, reStartTypingResult]);

  return (
    <>
      {showTutorial && (
        <Tutorial
          tutorialType={typingResult.tutorialType}
          tutorialEndCallback={handleJoyrideCallback}
        />
      )}
      {shouldShowSoundControl() && <SoundControlButton />}
      <div className="w-full h-screen flex items-center justify-center m-auto select-none">
        <div style={size} className="relative overflow-hidden">
          <DisplayWord
            themeType={themeType}
            character={character}
            gradeNumber={gradeNumber}
            typingProgress={typingProgress}
            score={calcScore()}
            limitTime={limitTime}
            resultType={resultType}
            handleGetStyle={(tailwindStyle) => getCurrentSizeStyle(tailwindStyle)}
            displayWord={currentDisplayWord}
            inputWord={currentInputWord}
            tutorialDisplayWord={showTutorial ? TUTORIAL_STEPS[typingResult.tutorialType][0]?.data.displayWord || '' : ''}
          />
          {!isFinishTyping && (
            <Keyboard shiftKeyDown={shiftKeyDown} inputErrorKey={inputErrorKey} remainingRomaji={typingProgress?.remainingRomaji ?? ''} />
          )}
          <StartModal
            typingResult={typingResult}
            isStartTyping={isStartTyping}
            startTypingCount={startTypingCount}
            handleGetStyle={(tailwindStyle) => getCurrentSizeStyle(tailwindStyle)}
            showTutorial={showTutorial}
          />
          {!isFinishTyping && limitTime != undefined && limitTime <= COUNT_DOWN_TIME && limitTime > 0 && (
            <div
              key={limitTime}
              className="absolute h-fit m-auto -top-[18%] left-0 bottom-0 right-0 flex justify-center text-[110px] font-black text-white text-stroke-3-black opacity-70 animate-shrink-fade"
            >
              {limitTime}
            </div>
          )}
          {isFinishTyping && (
            <ResultModal
              themeType={themeType}
              resultType={isFinishTyping ? judgeResultType(clearScore) : null}
              grade={typingResult}
              score={calcScore()}
              correctTypeCount={correctTypeCount}
              missTypeCount={unmatchTypeCount}
              handleReStart={() => reStartTypingResult()}
              handleGetStyle={(tailwindStyle) => getCurrentSizeStyle(tailwindStyle)}
              handleSetResultType={() => setResultType(isFinishTyping ? judgeResultType(clearScore) : undefined)}
            />
          )}
          {isStartTyping && limitTime === 0 && !isFinishTyping && (
            <img
              className="absolute w-[80%] object-contain m-auto top-0 left-0 bottom-0 right-0 animate-fade-in"
              src={`/common/finish.webp`}
              alt={'フィニッシュ'}
            />
          )}
          <div className="absolute top-0 w-full flex items-center justify-between z-40">
            <ChangeImageWhenHover
              handleClick={() => {
                playSeAudio(CLICK_BUTTON);
                finishTyping(true);
                navigate(`/practice/${themeType}/`);
              }}
              frameClassName={'w-[20%]'}
              src={'/common/buttons/back_home_button'}
              alt={'ホームにもどる'}
            />
            <ChangeImageWhenHover
              handleClick={() => {
                playSeAudio(CLICK_BUTTON);
                reStartTypingResult();
              }}
              frameClassName={'w-[20%]'}
              src={'/common/buttons/retry_button'}
              alt={'やりなおす'}
            />
          </div>
        </div>
        {isCountdownOrStartTyping && <UnloadBlocker />}
      </div>
      {isFinishTyping && <Event selectedThemeType={themeType} />}
    </>
  );
};
